import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Radio } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type LiveUpdate = { id: string; at: string; label?: string | null; body: string; author?: string };

const fmt = (iso: string) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

export const LiveUpdatesPanel = ({ articleId, initial, onChanged }: {
  articleId: string; initial: LiveUpdate[]; onChanged: () => void;
}) => {
  const [updates, setUpdates] = useState<LiveUpdate[]>(
    [...initial].sort((a, b) => (b.at || "").localeCompare(a.at || "")));
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const [at, setAt] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setLabel(""); setBody(""); setAt(""); setEditingId(null); };

  const submit = async () => {
    if (body.trim().length < 3) { toast.error("Update text is required"); return; }
    setBusy(true);
    const payload: any = { body: body.trim(), label: label.trim() || null };
    if (at) payload.at = new Date(at).toISOString();
    try {
      if (editingId) {
        const { data } = await api.patch(`/admin/articles/${articleId}/live-updates/${editingId}`, payload);
        setUpdates((u) => u.map((x) => (x.id === editingId ? data.update : x))
          .sort((a, b) => (b.at || "").localeCompare(a.at || "")));
        toast.success("Update edited");
      } else {
        const { data } = await api.post(`/admin/articles/${articleId}/live-updates`, payload);
        setUpdates((u) => [data.update, ...u].sort((a, b) => (b.at || "").localeCompare(a.at || "")));
        toast.success("Live update published");
      }
      reset();
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const startEdit = (u: LiveUpdate) => {
    setEditingId(u.id);
    setLabel(u.label || "");
    setBody(u.body);
    setAt("");
  };

  const remove = async (u: LiveUpdate) => {
    if (!window.confirm("Delete this live update?")) return;
    try {
      await api.delete(`/admin/articles/${articleId}/live-updates/${u.id}`);
      setUpdates((list) => list.filter((x) => x.id !== u.id));
      if (editingId === u.id) reset();
      toast.success("Update removed");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-lg border border-[#EF4444]/25 bg-[#140d0f] p-4" data-testid="live-updates-panel">
      <p className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#FCA5A5]">
        <Radio className="h-3.5 w-3.5" /> Live Updates
      </p>

      <div className="mt-3 space-y-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Optional headline — e.g. SEC releases statement"
          data-testid="live-update-label-input" className="bg-[#1A1A1A] border-white/10 text-white" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2}
          placeholder="Update text (required)…"
          data-testid="live-update-body-input" className="bg-[#1A1A1A] border-white/10 text-white" />
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px] text-gray-500">Timestamp override (optional)</Label>
            <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)}
              data-testid="live-update-time-input" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <Button onClick={submit} disabled={busy} data-testid="live-update-submit"
            className="bg-[#EF4444]/90 hover:bg-[#EF4444] text-white font-bold shrink-0">
            <Plus className="h-3.5 w-3.5 mr-1" /> {editingId ? "Save update" : "Add update"}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={reset} data-testid="live-update-cancel-edit"
              className="border-white/20 bg-transparent text-gray-300 shrink-0">Cancel</Button>
          )}
        </div>
      </div>

      {updates.length > 0 && (
        <ul className="mt-4 space-y-2.5 max-h-56 overflow-y-auto pr-1">
          {updates.map((u) => (
            <li key={u.id} data-testid={`admin-live-update-${u.id}`}
              className="rounded-md border border-white/[0.07] bg-black/25 px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-[9.5px] text-gray-500">
                    {fmt(u.at)}{u.author ? ` · ${u.author}` : ""}
                  </p>
                  {u.label && <p className="text-[12.5px] font-bold text-white mt-0.5">{u.label}</p>}
                  <p className="text-[12.5px] text-gray-300 leading-snug mt-0.5">{u.body}</p>
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(u)} title="Edit"
                    data-testid={`live-update-edit-${u.id}`} className="h-7 w-7 text-gray-400 hover:text-white">
                    <Pencil className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(u)} title="Delete"
                    data-testid={`live-update-delete-${u.id}`} className="h-7 w-7 text-gray-500 hover:text-red-400">
                    <Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
